import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, X, Users, Check, Search, ArrowUpDown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import { useProjectFilter } from "@/lib/projectFilter";
import { useRefresh } from "@/lib/useRefresh";
import { STATUS_LABEL, STATUS_COLOR, PRIORITY_COLOR, PRIORITY_LABEL, DIVISION_COLOR, fmtDate } from "@/lib/constants";
import CreateTaskDialog from "@/components/CreateTaskDialog";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import ProjectFilterChips from "@/components/ProjectFilterChips";
import DeadlineChip from "@/components/tasks/DeadlineChip";

const COLUMNS = ["todo", "ditolak", "menunggu_verifikasi", "selesai"];

export default function TasksPage() {
  const { user } = useAuth();
  const { selectedIds } = useProjectFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [highlightCommentId, setHighlightCommentId] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  // 149-cw-r134 (2026-08-31): Multi-select filter berdasarkan Assignee.
  // Ambil dari tasks yang sudah di-fetch (tidak butuh API tambahan). Persist
  // per user di localStorage supaya pilihan bertahan lintas reload.
  const storageKey = user?.id ? `tasks_assignee_filter_${user.id}` : "tasks_assignee_filter_anon";
  const [selectedAssignees, setSelectedAssignees] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]")); }
    catch { return new Set(); }
  });
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [assigneePopoverOpen, setAssigneePopoverOpen] = useState(false);
  // 149-cw-r142 (2026-08-31): Kanban sort mode. "manual" = urutan API (created_at desc),
  // "deadline" = deadline asc (paling urgent atas, null di bawah). Persist di localStorage.
  const sortStorageKey = user?.id ? `tasks_sort_${user.id}` : "tasks_sort_anon";
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem(sortStorageKey) || "deadline"; }
    catch { return "deadline"; }
  });
  const changeSortMode = (m) => {
    setSortMode(m);
    try { localStorage.setItem(sortStorageKey, m); } catch { /* noop */ }
  };

  // 149-cz (PROMPT 16-NOTIF-DEEPLINK-R2): honor deep-link query params.
  // /tasks?task=<id>&comment=<cid> — auto-open TaskDetailDialog on load,
  // survives refresh & direct link paste. Params dibersihkan setelah dibaca
  // supaya back button user tetap prediktabel.
  useEffect(() => {
    const t = searchParams.get("task");
    const c = searchParams.get("comment");
    if (t) {
      setOpenTask(t);
      setHighlightCommentId(c || null);
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // Fetch all authorised tasks (no server-side project_id filter). When one or more
  // projects are selected in the header multi-select, filter client-side. This lets
  // users combine several active projects into a single Tasks view.
  const load = async () => {
    const { data } = await api.get("/tasks");
    setTasks(data);
  };
  useEffect(() => { load(); }, []);
  useRefresh(load);

  const projectFiltered = selectedIds.length > 0
    ? tasks.filter((t) => selectedIds.includes(t.project_id))
    : tasks;

  // Daftar unique assignees (id, name, division) untuk multi-select popover.
  // Sumber: dari tasks yang ada — jadi hanya munculkan orang yang punya task.
  const assigneeOptions = useMemo(() => {
    const map = new Map();
    for (const t of projectFiltered) {
      if (!t.assignee_id || !t.assignee_name) continue;
      if (!map.has(t.assignee_id)) {
        map.set(t.assignee_id, {
          id: t.assignee_id,
          name: t.assignee_name,
          division: t.assignee_division || "",
        });
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [projectFiltered]);

  const filteredAssigneeOptions = useMemo(() => {
    const q = assigneeSearch.trim().toLowerCase();
    if (!q) return assigneeOptions;
    return assigneeOptions.filter((a) => (a.name + " " + a.division).toLowerCase().includes(q));
  }, [assigneeOptions, assigneeSearch]);

  const toggleAssignee = (id) => {
    setSelectedAssignees((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  };
  const clearAssignees = () => {
    setSelectedAssignees(new Set());
    try { localStorage.setItem(storageKey, "[]"); } catch { /* noop */ }
  };
  const selectAllAssignees = () => {
    const next = new Set(filteredAssigneeOptions.map((a) => a.id));
    setSelectedAssignees(next);
    try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* noop */ }
  };

  const visible = projectFiltered.filter((t) => {
    if (filter === "assigned_to_me" && t.assignee_id !== user.id) return false;
    if (filter === "created_by_me" && t.creator_id !== user.id) return false;
    if (selectedAssignees.size > 0 && !selectedAssignees.has(t.assignee_id)) return false;
    return true;
  });

  // 149-cw-r142: Sort visible tasks. Mode "deadline" = asc (urgent atas). Null → akhir.
  const sortedVisible = useMemo(() => {
    if (sortMode !== "deadline") return visible;
    const arr = [...visible];
    arr.sort((a, b) => {
      const da = a.deadline ? new Date(a.deadline).getTime() : Infinity;
      const db = b.deadline ? new Date(b.deadline).getTime() : Infinity;
      if (da !== db) return da - db;
      // Fallback: priority order kritis > tinggi > sedang > rendah
      const pMap = { Kritis: 0, Tinggi: 1, Sedang: 2, Rendah: 3 };
      const pa = pMap[a.priority] ?? 5;
      const pb = pMap[b.priority] ?? 5;
      return pa - pb;
    });
    return arr;
  }, [visible, sortMode]);

  const counts = {
    todo: visible.filter((t) => t.status === "todo").length,
    menunggu_verifikasi: visible.filter((t) => t.status === "menunggu_verifikasi").length,
    selesai: visible.filter((t) => t.status === "selesai").length,
    ditolak: visible.filter((t) => t.status === "ditolak").length,
    dibatalkan: visible.filter((t) => t.status === "dibatalkan").length,
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-slate-500">Manage</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Tasks</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-slate-300 text-xs" data-testid="task-sort-mode" title="Urutkan kartu Kanban">
            {[
              { k: "deadline", l: "Deadline", icon: ArrowUpDown },
              { k: "manual", l: "Terbaru", icon: null },
            ].map((s) => (
              <button key={s.k} data-testid={`sort-${s.k}`} onClick={() => changeSortMode(s.k)}
                className={`px-2.5 py-1.5 inline-flex items-center gap-1 uppercase tracking-wider font-semibold ${sortMode === s.k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {s.icon && <s.icon size={11} />} {s.l}
              </button>
            ))}
          </div>

          <div className="inline-flex border border-slate-300 text-xs" data-testid="task-filters">
            {[
              { k: "all", l: "All" },
              { k: "assigned_to_me", l: "For Me" },
              { k: "created_by_me", l: "Created By Me" },
            ].map((f) => (
              <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 uppercase tracking-wider font-semibold ${filter === f.k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {f.l}
              </button>
            ))}
          </div>

          {/* 149-cw-r134: Multi-select Assignee filter (Popover + checkbox). */}
          <Popover open={assigneePopoverOpen} onOpenChange={setAssigneePopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="btn-assignee-filter"
                className={`inline-flex items-center gap-1.5 h-[30px] px-3 border text-xs uppercase tracking-wider font-semibold transition-colors ${
                  selectedAssignees.size > 0
                    ? "border-[#0B1D3A] bg-[#0B1D3A] text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Users size={13} />
                {selectedAssignees.size === 0
                  ? "Semua Orang"
                  : selectedAssignees.size === 1
                    ? (assigneeOptions.find((a) => selectedAssignees.has(a.id))?.name.split(" ")[0] || "1 orang")
                    : `${selectedAssignees.size} orang dipilih`}
                {selectedAssignees.size > 0 && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); clearAssignees(); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); clearAssignees(); } }}
                    data-testid="btn-assignee-clear"
                    className="ml-1 inline-flex items-center justify-center hover:bg-white/20 p-0.5 cursor-pointer"
                    aria-label="Reset filter orang"
                  >
                    <X size={11} />
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={6} className="w-72 p-0 rounded-none" data-testid="assignee-filter-popover">
              <div className="border-b border-slate-200 p-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A]">Filter Berdasarkan Orang</div>
                  <button
                    type="button"
                    data-testid="btn-assignee-reset"
                    onClick={clearAssignees}
                    className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A]"
                  >
                    Reset
                  </button>
                </div>
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-2 text-slate-400" />
                  <input
                    type="text"
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    placeholder="Cari nama atau divisi…"
                    data-testid="assignee-filter-search"
                    className="w-full h-7 pl-7 pr-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none"
                  />
                </div>
                {filteredAssigneeOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={selectAllAssignees}
                    data-testid="btn-assignee-select-all"
                    className="mt-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline"
                  >
                    Pilih semua ({filteredAssigneeOptions.length})
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto">
                {filteredAssigneeOptions.length === 0 && (
                  <div className="p-4 text-center text-xs italic text-slate-400">Tidak ada orang yang cocok.</div>
                )}
                {filteredAssigneeOptions.map((a) => {
                  const on = selectedAssignees.has(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAssignee(a.id)}
                      data-testid={`assignee-option-${a.id}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-slate-100 hover:bg-slate-50 ${on ? "bg-slate-50" : "bg-white"}`}
                    >
                      <span className={`w-4 h-4 border inline-flex items-center justify-center shrink-0 ${on ? "bg-[#0B1D3A] border-[#0B1D3A]" : "border-slate-300"}`}>
                        {on && <Check size={11} className="text-white" />}
                      </span>
                      {a.division && (
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[a.division] || "bg-slate-600 text-white"}`}>{a.division}</span>
                      )}
                      <span className="text-slate-800 truncate flex-1">{a.name}</span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>

          <Button data-testid="btn-create-task" onClick={() => setOpenCreate(true)}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white uppercase text-xs tracking-wider font-semibold h-9">
            <Plus size={14} className="mr-1" /> New Task
          </Button>
        </div>
      </div>

      <ProjectFilterChips />

      {/* Status count bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="task-stats">
        {[
          { k: "todo", tone: "text-slate-700" },
          { k: "ditolak", tone: "text-yellow-700" },
          { k: "menunggu_verifikasi", tone: "text-amber-700" },
          { k: "selesai", tone: "text-emerald-700" },
          { k: "dibatalkan", tone: "text-slate-500" },
        ].map(({ k, tone }) => {
          const isActive = activeStatus === k;
          return (
            <button
              key={k}
              type="button"
              data-testid={`stat-${k}`}
              onClick={() => setActiveStatus(isActive ? null : k)}
              className={`text-left bg-white border px-3 py-2.5 transition-colors ${
                isActive ? "border-[#0B1D3A] ring-1 ring-[#0B1D3A]" : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <p className="overline text-slate-500 leading-none">{STATUS_LABEL[k]}</p>
              <p className={`font-display text-2xl tabular-nums tracking-tight mt-1 ${tone}`}>{counts[k]}</p>
            </button>
          );
        })}
      </div>

      {activeStatus && (
        <section className="bg-white border border-slate-200" data-testid={`table-status-${activeStatus}`}>
          <div className="flex items-center justify-between px-2 py-2 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${STATUS_COLOR[activeStatus]}`}>
                {STATUS_LABEL[activeStatus]}
              </span>
              <span className="text-xs text-slate-500 tabular-nums">{counts[activeStatus]} task{counts[activeStatus] === 1 ? "" : "s"}</span>
            </div>
            <button
              data-testid="close-status-table"
              onClick={() => setActiveStatus(null)}
              className="text-slate-500 hover:text-[#0B1D3A] flex items-center gap-1 text-xs uppercase tracking-wider font-semibold"
            >
              <X size={14} /> Close
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-auto">
              <thead className="bg-slate-50 text-slate-500">
                <tr className="text-[10px] uppercase tracking-wider">
                  <th className="text-left px-2 py-2 font-semibold">Title</th>
                  <th className="text-left px-2 py-2 font-semibold">Project</th>
                  <th className="text-left px-2 py-2 font-semibold">WO / PO</th>
                  <th className="text-left px-2 py-2 font-semibold">Assignee</th>
                  <th className="text-left px-2 py-2 font-semibold">Priority</th>
                  <th className="text-left px-2 py-2 font-semibold whitespace-nowrap">Deadline</th>
                </tr>
              </thead>
              <tbody>
                {sortedVisible.filter((t) => t.status === activeStatus).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-xs text-slate-400 italic">No tasks in this status.</td>
                  </tr>
                )}
                {sortedVisible.filter((t) => t.status === activeStatus).map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setOpenTask(t.id)}
                    data-testid={`row-task-${t.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-2 py-2">
                      <div className="font-semibold text-[#0B1D3A] leading-snug">{t.title}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">by {t.creator_name}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="text-slate-700 truncate max-w-[220px]" title={t.parent_project_name || t.project_name}>
                        {t.parent_project_name || t.project_name || "—"}
                      </div>
                      {t.project_type === "working_order" && (
                        <span className="inline-block mt-0.5 text-[9px] font-semibold uppercase tracking-wider border border-indigo-300 text-indigo-700 bg-indigo-50 px-1">WO</span>
                      )}
                      {t.project_type === "variation_order" && (
                        <span className="inline-block mt-0.5 text-[9px] font-semibold uppercase tracking-wider border border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50 px-1">VO</span>
                      )}
                      {t.wo_phase && (
                        <span className="inline-block mt-0.5 ml-1 text-[9px] font-semibold uppercase tracking-wider border border-sky-300 text-sky-700 bg-sky-50 px-1">
                          {({"survey_sph":"Survey & SPH","project":"Project","invoice":"Invoice"})[t.wo_phase] || t.wo_phase}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {t.project_type === "working_order" ? (
                        <div className="space-y-0.5">
                          {t.wo_number && <div><span className="overline text-slate-400 mr-1">WO</span>{t.wo_number}{t.wo_date ? ` · ${fmtDate(t.wo_date)}` : ""}</div>}
                          {t.po_number && <div><span className="overline text-slate-400 mr-1">PO</span>{t.po_number}{t.po_date ? ` · ${fmtDate(t.po_date)}` : ""}</div>}
                          {!t.wo_number && !t.po_number && <span className="text-slate-400">—</span>}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[t.assignee_division]}`}>{t.assignee_division}</span>
                        <span className="text-slate-700">{t.assignee_name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold ${PRIORITY_COLOR[t.priority]}`}>{PRIORITY_LABEL[t.priority]}</span>
                    </td>
                    <td className="px-2 py-2 tabular-nums text-slate-700 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 justify-end">
                        <DeadlineChip deadline={t.deadline} status={t.status} size="xs" />
                        <span>{fmtDate(t.deadline)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => {
          const items = sortedVisible.filter((t) => t.status === col);
          return (
            <div key={col} className="kanban-col p-3" data-testid={`col-${col}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${STATUS_COLOR[col]}`}>{STATUS_LABEL[col]}</span>
                <span className="text-xs tabular-nums text-slate-500">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <div className="text-xs text-slate-400 italic p-2">Empty</div>}
                {items.map((t) => (
                  <button key={t.id} onClick={() => setOpenTask(t.id)} data-testid={`task-card-${t.id}`}
                    className="w-full text-left bg-white border border-slate-200 hover:border-[#0B1D3A] p-3 space-y-2 transition-colors">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[t.assignee_division]}`}>{t.assignee_division}</span>
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold ${PRIORITY_COLOR[t.priority]}`}>{PRIORITY_LABEL[t.priority]}</span>
                      {t.project_type === "working_order" && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border border-indigo-300 text-indigo-700 bg-indigo-50">WO</span>
                      )}
                      {t.project_type === "variation_order" && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">VO</span>
                      )}
                      {t.wo_phase && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border border-sky-300 text-sky-700 bg-sky-50">
                          {({"survey_sph":"Survey & SPH","project":"Project","invoice":"Invoice"})[t.wo_phase] || t.wo_phase}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-sm text-[#0B1D3A] leading-snug line-clamp-2">{t.title}</div>
                    {(t.project_name || t.parent_project_name) && (
                      <div className="text-[10px] text-slate-500 truncate" title={t.parent_project_name || t.project_name}>
                        {t.parent_project_name || t.project_name}
                      </div>
                    )}
                    {t.project_type === "working_order" && (t.wo_number || t.po_number) && (
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-slate-600 border-t border-slate-100 pt-1.5">
                        {t.wo_number && (
                          <div className="truncate"><span className="overline text-slate-400">WO</span> {t.wo_number}</div>
                        )}
                        {t.wo_date && (
                          <div className="truncate tabular-nums text-right"><span className="overline text-slate-400">WO Date</span> {fmtDate(t.wo_date)}</div>
                        )}
                        {t.po_number && (
                          <div className="truncate"><span className="overline text-slate-400">PO</span> {t.po_number}</div>
                        )}
                        {t.po_date && (
                          <div className="truncate tabular-nums text-right"><span className="overline text-slate-400">PO Date</span> {fmtDate(t.po_date)}</div>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-slate-500 flex items-center justify-between gap-2">
                      <span className="truncate">→ {t.assignee_name.split(" ")[0]}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <DeadlineChip deadline={t.deadline} status={t.status} />
                        <span className="tabular-nums">{fmtDate(t.deadline)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <CreateTaskDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
      <TaskDetailDialog taskId={openTask} highlightCommentId={highlightCommentId} open={!!openTask} onOpenChange={(o) => { if (!o) { setOpenTask(null); setHighlightCommentId(null); const next = new URLSearchParams(searchParams); next.delete("task"); next.delete("comment"); setSearchParams(next, { replace: true }); } }} onChanged={load} />
    </div>
  );
}
